import { useState, useRef } from "react";
import { useProgressReport, type ReportType } from "../../hooks/useProgressReport";
import { Loader } from "../Loader";
import { ReportHeader } from "./ReportHeader";
import { SummaryStats } from "./SummaryStats";
import { DisciplineBreakdown } from "./DisciplineBreakdown";
import { BadgesSection } from "./BadgesSection";
import { StreakCard } from "./StreakCard";
import { InsightsSection } from "./InsightsSection";
import { ComparisonSection } from "./ComparisonSection";

export interface ProgressReportProps {
  childId: string;
  initialReportType?: ReportType;
  onClose?: () => void;
}

/**
 * Complete progress report component with all sections.
 * Features a beautiful, print-friendly layout with weekly/monthly views.
 */
export function ProgressReport({
  childId,
  initialReportType = "weekly",
  onClose,
}: ProgressReportProps) {
  const [reportType, setReportType] = useState<ReportType>(initialReportType);
  const reportRef = useRef<HTMLDivElement>(null);
  const { data, isLoading, error, refetch } = useProgressReport(childId, reportType);

  // Handle print
  const handlePrint = () => {
    window.print();
  };

  // Handle PDF export (using browser print-to-PDF)
  const handleExport = () => {
    // Trigger print dialog which allows saving as PDF
    window.print();
  };

  // Handle report type change
  const handleTypeChange = (type: ReportType) => {
    setReportType(type);
  };

  // Loading state
  if (isLoading) {
    return (
      <div className="min-h-[400px] flex items-center justify-center">
        <div className="text-center">
          <Loader size="lg" />
          <p className="font-body text-neutral-600 mt-4">Loading report...</p>
        </div>
      </div>
    );
  }

  // Error state
  if (error) {
    return (
      <div className="min-h-[400px] flex flex-col items-center justify-center p-8">
        <div className="text-center max-w-md">
          <span className="text-5xl mb-4 block">😢</span>
          <h2 className="font-display font-bold text-display-sm text-neutral-800 mb-2">
            Couldn't load report
          </h2>
          <p className="font-body text-neutral-600 mb-6">{error}</p>
          <button
            onClick={() => refetch()}
            className="px-6 py-3 bg-magic-500 hover:bg-magic-600 text-cloud rounded-xl font-body font-medium transition-colors"
          >
            Try Again
          </button>
        </div>
      </div>
    );
  }

  // No data state
  if (!data) {
    return (
      <div className="min-h-[400px] flex items-center justify-center p-8">
        <div className="text-center">
          <span className="text-5xl mb-4 block opacity-60">📊</span>
          <p className="font-body text-neutral-600">No report data available.</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-b from-neutral-50 to-magic-50/10 print:bg-white print:min-h-0">
      {/* Print styles */}
      <style>
        {`
          @media print {
            body {
              -webkit-print-color-adjust: exact !important;
              print-color-adjust: exact !important;
            }
            .print\\:hidden {
              display: none !important;
            }
            .print\\:shadow-none {
              box-shadow: none !important;
            }
            .print\\:border-neutral-200 {
              border-color: #E2DDD6 !important;
            }
            .print\\:p-4 {
              padding: 1rem !important;
            }
            .print\\:rounded-none {
              border-radius: 0 !important;
            }
            .print\\:bg-white {
              background: white !important;
            }
            .print\\:min-h-0 {
              min-height: 0 !important;
            }
            .print\\:hover\\:scale-100:hover {
              transform: scale(1) !important;
            }
          }
        `}
      </style>

      {/* Close button (when in modal mode) */}
      {onClose && (
        <button
          onClick={onClose}
          className="fixed top-4 right-4 z-50 p-2 rounded-full bg-cloud hover:bg-neutral-100 shadow-md transition-colors print:hidden"
          aria-label="Close report"
        >
          <svg className="w-6 h-6 text-neutral-600" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
            <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
          </svg>
        </button>
      )}

      {/* Report content */}
      <div ref={reportRef} className="max-w-2xl mx-auto px-4 py-8 print:py-4 print:px-0">
        {/* Header */}
        <ReportHeader
          childName={data.childName}
          periodLabel={data.period.label}
          reportType={reportType}
          onTypeChange={handleTypeChange}
          onExport={handleExport}
          onPrint={handlePrint}
        />

        {/* Main sections */}
        <div className="mt-6 space-y-8 print:space-y-6">
          {/* Summary Stats */}
          <SummaryStats summary={data.summary} comparison={data.comparison} />

          {/* Insights (at the top for visibility) */}
          {data.insights.length > 0 && (
            <InsightsSection insights={data.insights} />
          )}

          {/* Discipline Breakdown */}
          <DisciplineBreakdown
            disciplines={data.disciplineBreakdown}
            totalStars={data.summary.starsEarned}
          />

          {/* Two-column layout for streak and badges on larger screens */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 print:grid-cols-1">
            {/* Streak Card */}
            <StreakCard streakInfo={data.streakInfo} />

            {/* Badges Section */}
            <BadgesSection badges={data.badgesEarnedInPeriod} />
          </div>

          {/* Period Comparison */}
          <ComparisonSection
            comparison={data.comparison}
            reportType={reportType}
          />
        </div>

        {/* Footer */}
        <div className="mt-8 pt-6 border-t border-neutral-200 text-center print:mt-6 print:pt-4">
          <p className="font-body text-sm text-neutral-500">
            JumpBean Progress Report • Generated{" "}
            {new Date().toLocaleDateString("en-US", {
              month: "long",
              day: "numeric",
              year: "numeric",
            })}
          </p>
          <p className="font-body text-xs text-neutral-400 mt-1">
            Learning is an adventure! 🌟
          </p>
        </div>
      </div>
    </div>
  );
}

export default ProgressReport;
